import {
  AfterViewInit,
  Directive,
  ElementRef,
  inject,
  Input,
  OnDestroy,
} from '@angular/core';

@Directive({
  selector: '[siatEqualStatusBadgeWidth]',
  standalone: true,
})
export class EqualStatusBadgeWidthDirective
  implements AfterViewInit, OnDestroy
{
  @Input() statusBadgeSelector = '.status-badge';
  @Input() statusBadgeWidthVar = '--siat-context-status-badge-width';

  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private mutationObserver?: MutationObserver;
  private animationFrame: number | null = null;

  ngAfterViewInit(): void {
    const host = this.elementRef.nativeElement;

    this.mutationObserver = new MutationObserver(() => {
      this.scheduleResize();
    });
    this.mutationObserver.observe(host, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    this.scheduleResize();
  }

  ngOnDestroy(): void {
    this.mutationObserver?.disconnect();

    if (this.animationFrame !== null) {
      cancelAnimationFrame(this.animationFrame);
    }
  }

  private scheduleResize(): void {
    if (typeof requestAnimationFrame === 'undefined') return;

    if (this.animationFrame !== null) {
      cancelAnimationFrame(this.animationFrame);
    }

    this.animationFrame = requestAnimationFrame(() => {
      this.animationFrame = requestAnimationFrame(() => {
        this.animationFrame = null;
        this.resizeBadges();
      });
    });
  }

  private resizeBadges(): void {
    const host = this.elementRef.nativeElement;
    const badges = this.badges;

    if (!badges.length) {
      host.style.removeProperty(this.statusBadgeWidthVar);
      return;
    }

    host.style.removeProperty(this.statusBadgeWidthVar);
    const maxWidth = Math.max(...badges.map(badge => this.measure(badge)));
    host.style.setProperty(this.statusBadgeWidthVar, `${maxWidth}px`);
  }

  private measure(element: HTMLElement): number {
    const clone = element.cloneNode(true) as HTMLElement;
    const measurableElements = [
      clone,
      ...Array.from(
        clone.querySelectorAll<HTMLElement>(
          [
            '.mdc-evolution-chip__cell',
            '.mdc-evolution-chip__cell--primary',
            '.mdc-evolution-chip__action',
            '.mdc-evolution-chip__action--primary',
            '.mdc-evolution-chip__text-label',
          ].join(',')
        )
      ),
    ];

    clone.setAttribute('aria-hidden', 'true');
    clone.style.position = 'fixed';
    clone.style.left = '-9999px';
    clone.style.top = '-9999px';
    clone.style.visibility = 'hidden';
    clone.style.pointerEvents = 'none';

    measurableElements.forEach(item => {
      item.style.width = 'max-content';
      item.style.minWidth = 'max-content';
      item.style.maxWidth = 'none';
      item.style.overflow = 'visible';
    });

    document.body.appendChild(clone);
    const measuredWidth = Math.ceil(clone.getBoundingClientRect().width);
    clone.remove();

    return measuredWidth;
  }

  private get badges(): HTMLElement[] {
    return Array.from(
      this.elementRef.nativeElement.querySelectorAll<HTMLElement>(
        this.statusBadgeSelector
      )
    );
  }
}
